import axios from 'axios';
import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';
import { saveAs } from 'file-saver';

// Function to format the date to dd-mm-yy
const formatDate = (date) => {
  const d = new Date(date);
  const day = ('0' + d.getDate()).slice(-2);
  const month = ('0' + (d.getMonth() + 1)).slice(-2);
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

// Function to format time from 24-hour format (21:00) to French format (21h00)
const formatTime = (time) => {
  if (!time) return '';
  // Remove any AM/PM indicators and extra spaces
  const cleanTime = time.replace(/\s*(AM|PM|am|pm)\s*/gi, '').trim();
  // Replace colon with 'h' for French format
  return cleanTime.replace(':', 'h');
};

// Test the formatTime function
console.log('🕐 Testing formatTime function:');
console.log('🕐 21:00 ->', formatTime('21:00'));
console.log('🕐 08:30 ->', formatTime('08:30'));
console.log('🕐 20:30 ->', formatTime('20:30'));
console.log('🕐 21:00 AM ->', formatTime('21:00 AM'));
console.log('🕐 08:00 PM ->', formatTime('08:00 PM'));

// Modern DOCX generation function
export const generateContract = async (formData) => {
  try {
    console.log('🔄 Starting DOCX generation...');
    console.log('🎤 FormData singers:', formData.singers);
    console.log('🎤 Singers type:', typeof formData.singers);
    console.log('🎤 Singers length:', formData.singers?.length);
    console.log('🎤 Singers JSON stringify:', JSON.stringify(formData.singers, null, 2));
    if (formData.singers && formData.singers.length > 0) {
      console.log('🎤 First singer:', formData.singers[0]);
      console.log('🎤 First singer type:', typeof formData.singers[0]);
      console.log('🎤 First singer keys:', Object.keys(formData.singers[0] || {}));
      console.log('🎤 First singer JSON:', JSON.stringify(formData.singers[0], null, 2));
    }
    
    // Fetch the DOCX template file
    const response = await axios.get('/test.docx', { responseType: 'arraybuffer' });
    
    if (!response.data) {
      throw new Error('Template file is empty or not found');
    }
    
    // Load the DOCX into PizZip
    const zip = new PizZip(response.data);
    
    // Create Docxtemplater instance with modern options
    const doc = new Docxtemplater(zip, {
      paragraphLoop: true,
      linebreaks: true
    });
    
    // Prepare the replacements
    let singersString = 'Aucun chanteur';
    
    if (formData.singers && formData.singers.length > 0) {
      try {
        // Handle different possible data structures
        const singerNames = formData.singers.map((singer, index) => {
          console.log(`🎤 Processing singer ${index}:`, singer);
          console.log(`🎤 Singer ${index} type:`, typeof singer);
          console.log(`🎤 Singer ${index} keys:`, typeof singer === 'object' ? Object.keys(singer) : 'N/A');
          
          if (typeof singer === 'string') {
            console.log(`🎤 Singer ${index} is string:`, singer);
            return singer; // Already a name string
          } else if (typeof singer === 'object' && singer !== null) {
            // Try different possible property names for the name
            const name = singer.name || singer.Name || singer.singerName || singer.fullName || singer.toString() || 'Unknown Singer';
            console.log(`🎤 Singer ${index} extracted name:`, name);
            console.log(`🎤 Singer ${index} toString():`, singer.toString());
            return name;
          } else {
            console.log(`🎤 Singer ${index} is unknown type:`, singer);
            return 'Unknown Singer';
          }
        });
        singersString = singerNames.join('\n\t\t\t\t\t-');
      } catch (error) {
        console.error('Error processing singers:', error);
        singersString = 'Error processing singers';
      }
    }
    
    console.log('🎤 Processed singers string:', singersString);
    
    // Debug time formatting
    console.log('🕐 Original startTime:', formData.startTime);
    console.log('🕐 Formatted startTime:', formatTime(formData.startTime));
    console.log('🕐 Original endTime:', formData.endTime);
    console.log('🕐 Formatted endTime:', formatTime(formData.endTime));
    
    const replacements = {
      clientName: formData.clientName || '',
      clientCin: formData.clientCin || '',
      clientTel: formData.clientTel || '',
      location: formData.location || '',
      price: formData.price || '',
      prepayment: formData.prepayment || '',
      paymentLeft: formData.paymentLeft || '',
      prepaymentDate: formatDate(formData.prepaymentDate) || '',
      eventDate: formatDate(formData.eventDate) || '',
      contractDate: formatDate(formData.contractDate) || '',
      startTime: formatTime(formData.startTime) || '',
      endTime: formatTime(formData.endTime) || '',
      numberMusicians: formData.numberMusicians || '',
      description: formData.description || '',
      singers: singersString
    };
    
    console.log('📝 Applying replacements:', replacements);
    
    // Use the modern render method with data
    doc.render(replacements);
    
    // Generate the document
    const output = doc.getZip().generate({ type: 'blob' });
    
    // Create filename
    const sanitizedClientName = (formData.clientName || 'Client').replace(/[^a-zA-Z0-9-_]/g, '_');
    const contractId = formData.contractId || Date.now();
    const fileName = `${sanitizedClientName}_${contractId}.docx`;
    
    console.log('📁 Filename generation:', {
      clientName: formData.clientName,
      contractId: formData.contractId,
      fallbackId: Date.now(),
      finalContractId: contractId,
      fileName: fileName
    });
    
    // Save the file
    saveAs(output, fileName);
    
    console.log('✅ DOCX generated successfully:', fileName);
    
    // Try to upload to server (optional)
    try {
      await uploadContractToServer(output, fileName);
      console.log('✅ Contract uploaded to server');
    } catch (uploadError) {
      console.warn('⚠️ Upload failed, but document was generated locally:', uploadError.message);
    }
    
  } catch (error) {
    console.error('❌ DOCX generation failed:', error.message);
    throw error;
  }
};

// Upload function
const uploadContractToServer = async (file, fileName) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    const formData = new FormData();
    formData.append('contract', file, fileName);
    
    await axios.post('https://managementapp-api.onrender.com/api/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`
      }
    });
    
  } catch (error) {
    console.error('Upload error:', error.message);
    throw error;
  }
};

// Function to fetch singers by their IDs (restored)
const fetchSingersByIds = async (singerIds) => {
  try {
    if (!singerIds || singerIds.length === 0) {
      return [];
    }
    
    const requests = singerIds.map(id => axios.get(`https://managementapp-api.onrender.com/api/singers/${id}`));
    const responses = await Promise.allSettled(requests);

    // Extract names from fulfilled requests
    return responses
      .filter(res => res.status === 'fulfilled')
      .map(res => res.value.data.name);

  } catch (error) {
    console.error('Error fetching singers by IDs:', error.response?.data || error.message);
    return [];
  }
};
